import "./AiOutputFeedbackControls.scss";

import { useId, useMemo, useState } from "react";

import {
  type AiOutputFeedbackOutputType,
  type AiOutputFeedbackSentiment,
  useAiOutputFeedbackReasons,
  useSubmitAiOutputFeedback,
} from "../../api/aiOutputFeedback";
import { isApiError } from "../../api/errors";

const MAX_EXPLANATION_LENGTH = 4000;

export interface AiOutputFeedbackControlsProps {
  executionId?: number | null;
  outputType: AiOutputFeedbackOutputType;
  className?: string;
}

export function AiOutputFeedbackControls({
  executionId,
  outputType,
  className,
}: AiOutputFeedbackControlsProps) {
  if (executionId == null || !Number.isSafeInteger(executionId) || executionId <= 0) {
    return null;
  }

  return (
    <AiOutputFeedbackControlsContent
      executionId={executionId}
      outputType={outputType}
      className={className}
    />
  );
}

function AiOutputFeedbackControlsContent({
  executionId,
  outputType,
  className,
}: {
  executionId: number;
  outputType: AiOutputFeedbackOutputType;
  className?: string;
}) {
  const [isDownFormOpen, setIsDownFormOpen] = useState(false);
  const [reasonCode, setReasonCode] = useState("");
  const [explanationText, setExplanationText] = useState("");
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [submittedSentiment, setSubmittedSentiment] = useState<AiOutputFeedbackSentiment | null>(
    null
  );
  const reasonFieldsetId = useId();
  const explanationId = useId();
  const reasonsQuery = useAiOutputFeedbackReasons(isDownFormOpen);
  const submitFeedback = useSubmitAiOutputFeedback();

  const reasons = useMemo(
    () =>
      (reasonsQuery.data?.data ?? []).filter(
        (reason) =>
          reason.isActive && (reason.applicableTo === "both" || reason.applicableTo === outputType)
      ),
    [outputType, reasonsQuery.data?.data]
  );
  const reasonsLoadErrorMessage = reasonsQuery.isError
    ? isApiError(reasonsQuery.error) && reasonsQuery.error.status === 403
      ? "You do not have permission to submit feedback."
      : "Unable to load feedback reasons. Please try again."
    : null;
  const isInsight = outputType === "ai_insight";
  const trimmedExplanation = explanationText.trim();
  const canSubmitDown =
    reasonCode !== "" &&
    explanationText.length <= MAX_EXPLANATION_LENGTH &&
    (!isInsight || trimmedExplanation.length > 0) &&
    !submitFeedback.isPending;

  function clearDownForm() {
    setIsDownFormOpen(false);
    setReasonCode("");
    setExplanationText("");
    setValidationMessage(null);
  }

  function submit(
    sentiment: AiOutputFeedbackSentiment,
    selectedReasonCode?: string,
    selectedExplanationText?: string
  ) {
    submitFeedback.mutate(
      {
        executionId,
        sentiment,
        ...(selectedReasonCode ? { reasonCode: selectedReasonCode } : {}),
        ...(selectedExplanationText ? { explanationText: selectedExplanationText } : {}),
      },
      {
        onSuccess: () => {
          setSubmittedSentiment(sentiment);
          if (sentiment === "down") clearDownForm();
        },
      }
    );
  }

  function handleDownSubmit() {
    if (!reasonCode) {
      setValidationMessage("Select a reason for your feedback.");
      return;
    }
    if (explanationText.length > MAX_EXPLANATION_LENGTH) {
      setValidationMessage(`Explanation must be ${MAX_EXPLANATION_LENGTH} characters or fewer.`);
      return;
    }
    if (isInsight && !trimmedExplanation) {
      setValidationMessage("An explanation is required for AI Insight feedback.");
      return;
    }

    setValidationMessage(null);
    submit("down", reasonCode, trimmedExplanation || undefined);
  }

  const errorMessage =
    validationMessage ??
    (submitFeedback.isError
      ? isApiError(submitFeedback.error)
        ? submitFeedback.error.message
        : "Unable to submit feedback. Please try again."
      : null);

  return (
    <div className={["ai-output-feedback", className].filter(Boolean).join(" ")}>
      <div className="ai-output-feedback__actions" aria-label="Rate this AI response">
        <button
          type="button"
          className="ai-output-feedback__button"
          onClick={() => submit("up")}
          disabled={submitFeedback.isPending}
          aria-label="Helpful"
        >
          <span className="Appkit4-icon icon-thumb-up-outline" aria-hidden="true" />
        </button>
        <button
          type="button"
          className="ai-output-feedback__button"
          onClick={() => {
            setSubmittedSentiment(null);
            setValidationMessage(null);
            setIsDownFormOpen((open) => !open);
          }}
          disabled={submitFeedback.isPending}
          aria-expanded={isDownFormOpen}
          aria-controls={isDownFormOpen ? reasonFieldsetId : undefined}
          aria-label="Not helpful"
        >
          <span className="Appkit4-icon icon-thumb-down-outline" aria-hidden="true" />
        </button>
      </div>

      {submittedSentiment != null && (
        <p className="ai-output-feedback__status" role="status">
          Thanks for your feedback.
        </p>
      )}

      {isDownFormOpen && (
        <div
          className="ai-output-feedback__form"
          role="dialog"
          aria-label="Tell us what could be improved"
        >
          <fieldset
            id={reasonFieldsetId}
            className="ai-output-feedback__reasons"
            disabled={submitFeedback.isPending}
          >
            <legend>What could be improved?</legend>
            {reasonsQuery.isLoading ? (
              <p>Loading feedback reasons…</p>
            ) : reasonsLoadErrorMessage ? (
              <p role="alert">{reasonsLoadErrorMessage}</p>
            ) : reasons.length > 0 ? (
              reasons.map((reason) => (
                <label key={reason.id} className="ai-output-feedback__reason">
                  <input
                    type="radio"
                    name={reasonFieldsetId}
                    value={reason.reasonCode}
                    checked={reasonCode === reason.reasonCode}
                    onChange={(event) => setReasonCode(event.target.value)}
                  />
                  {reason.displayText}
                </label>
              ))
            ) : (
              <p>No feedback reasons are currently available.</p>
            )}
          </fieldset>

          <label htmlFor={explanationId} className="ai-output-feedback__explanation-label">
            Explanation {isInsight ? "(required)" : "(optional)"}
          </label>
          <textarea
            id={explanationId}
            className="ai-output-feedback__explanation"
            value={explanationText}
            maxLength={MAX_EXPLANATION_LENGTH}
            aria-describedby={`${explanationId}-count`}
            onChange={(event) => setExplanationText(event.target.value)}
          />
          <p
            id={`${explanationId}-count`}
            className="ai-output-feedback__character-count"
            aria-live="polite"
          >
            {explanationText.length}/{MAX_EXPLANATION_LENGTH}
          </p>

          {errorMessage != null && (
            <p className="ai-output-feedback__error" role="alert">
              {errorMessage}
            </p>
          )}

          <div className="ai-output-feedback__form-actions">
            <button type="button" className="ai-output-feedback__cancel" onClick={clearDownForm}>
              Cancel
            </button>
            <button
              type="button"
              className="ai-output-feedback__submit"
              onClick={handleDownSubmit}
              disabled={!canSubmitDown}
            >
              Submit feedback
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
